/**
 * Сторінка підтримки клієнта
 * @component SupportPage
 * @file SupportPage.tsx
 * @path frontend/src/pages/SupportPage.tsx
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Badge,
  Button,
  TextField,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccessTime as ClockIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Help as HelpIcon,
  ContactMail as ContactMailIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import SupportChatModal from '../../modals/SupportChatModal/SupportChatModal';

interface SupportMessage {
  id: string;
  user_id: string;
  category: string;
  message: string;
  status: string;
  created_at: string;
  updated_at: string;
  admin_response?: {
    String: string;
    Valid: boolean;
  };
  responded_at?: {
    String: string;
    Valid: boolean;
  };
  conversation?: Array<{
    id: string;
    sender_type: string;
    sender_id?: string;
    sender_name?: string;
    message: string;
    created_at: string;
  }>;
  user_name?: string; // Available when getting user's own messages
}

interface SupportSettings {
  phone: string;
  email: string;
  working_hours: string;
}

const SupportPage: React.FC = () => {
  const { language } = useLanguage();
  const {} = useAuth();
  const { addNotification } = useNotification();
  const [activeTab, setActiveTab] = useState<'contact' | 'history' | 'faq'>('contact');
  const [selectedCategory, setSelectedCategory] = useState<string | null>('technical');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<SupportMessage | null>(null);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [supportSettings, setSupportSettings] = useState<SupportSettings>({
    phone: '+38 (044) 123-45-67',
    email: 'support@visit-platform.com',
    working_hours: 'Пн-Пт: 9:00-18:00'
  });

  const supportCategories = [
    {
      id: 'booking',
      title: language === 'ua' ? 'Бронювання записів' : 'Appointment Booking',
      description: language === 'ua' ? 'Питання щодо бронювання, скасування або зміни записів' : 'Questions about booking, canceling or changing appointments',
      icon: 'bi-calendar-check'
    },
    {
      id: 'payment',
      title: language === 'ua' ? 'Оплата та рахунки' : 'Payment & Billing',
      description: language === 'ua' ? 'Питання щодо оплати, рахунків та повернень коштів' : 'Questions about payments, invoices and refunds',
      icon: 'bi-credit-card'
    },
    {
      id: 'account',
      title: language === 'ua' ? 'Обліковий запис' : 'Account Issues',
      description: language === 'ua' ? 'Проблеми з входом, профілем або налаштуваннями' : 'Login issues, profile or settings problems',
      icon: 'bi-person'
    },
    {
      id: 'services',
      title: language === 'ua' ? 'Послуги та майстри' : 'Services & Masters',
      description: language === 'ua' ? 'Інформація про послуги, майстрів та доступність' : 'Information about services, masters and availability',
      icon: 'bi-scissors'
    },
    {
      id: 'technical',
      title: language === 'ua' ? 'Технічні проблеми' : 'Technical Issues',
      description: language === 'ua' ? 'Проблеми з додатком, сайтом або підключенням' : 'App issues, website problems or connection issues',
      icon: 'bi-gear'
    },
    {
      id: 'other',
      title: language === 'ua' ? 'Інше' : 'Other',
      description: language === 'ua' ? 'Будь-які інші питання або пропозиції' : 'Any other questions or suggestions',
      icon: 'bi-question-circle'
    }
  ];

  const faqItems = [
    {
      question: language === 'ua' ? 'Як скасувати запис?' : 'How to cancel an appointment?',
      answer: language === 'ua'
        ? 'Перейдіть до розділу "Мої записи", знайдіть потрібний запис та натисніть "Скасувати". Скасування можливо не пізніше ніж за 24 години до візиту.'
        : 'Go to "My Appointments" section, find the appointment and click "Cancel". Cancellation is possible no later than 24 hours before the visit.'
    },
    {
      question: language === 'ua' ? 'Як змінити час запису?' : 'How to change appointment time?',
      answer: language === 'ua'
        ? 'Скасуйте поточний запис та створіть новий з бажаним часом. Або зв\'яжіться з майстром безпосередньо через додаток.'
        : 'Cancel the current appointment and create a new one with the desired time. Or contact the master directly through the app.'
    },
    {
      question: language === 'ua' ? 'Що робити, якщо я запізнююся?' : "What to do if I'm late?",
      answer: language === 'ua'
        ? 'Повідомте майстра заздалегідь через додаток або по телефону. Майстри зазвичай мають гнучкий графік, але підтвердіть це перед візитом.'
        : 'Inform the master in advance through the app or by phone. Masters usually have flexible schedules, but confirm this before your visit.'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory || !message.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/v1/support/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          category: selectedCategory,
          message: message.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      const result = await response.json();
      console.log('Support message sent successfully:', result);

      // Reset form
      setSelectedCategory('technical');
      setMessage('');

      // Refresh messages list
      fetchMessages();

      // Show success message on page
      setShowSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting support request:', error);
      addNotification(language === 'ua' ? 'Помилка при надсиланні повідомлення. Спробуйте ще раз.' : 'Error sending message. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchMessages = async () => {
    setIsLoadingMessages(true);
    try {
      const response = await fetch('/api/v1/support/messages', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch messages');
      }

      const result = await response.json();
      setMessages(result.messages || []);
    } catch (error) {
      console.error('Error fetching support messages:', error);
      addNotification(language === 'ua' ? 'Помилка при завантаженні повідомлень' : 'Error loading messages', 'error');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/v1/support/settings', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch settings');
      }

      const result = await response.json();
      setSupportSettings(result);
    } catch (error) {
      console.error('Error fetching support settings:', error);
      // Keep default values on error
    }
  };

  useEffect(() => {
    fetchMessages();
    fetchSettings();
  }, []);

  // Update selectedMessage when messages array changes
  useEffect(() => {
    if (selectedMessage && messages.length > 0) {
      const updatedMessage = messages.find(m => m.id === selectedMessage.id);
      if (updatedMessage && 
          (updatedMessage.conversation?.length !== selectedMessage.conversation?.length ||
           updatedMessage.updated_at !== selectedMessage.updated_at)) {
        setSelectedMessage(updatedMessage);
      }
    }
  }, [messages, selectedMessage?.id]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return language === 'ua' ? 'Нове' : 'New';
      case 'in_progress':
        return language === 'ua' ? 'В роботі' : 'In Progress';
      case 'resolved':
        return language === 'ua' ? 'Вирішене' : 'Resolved';
      case 'closed':
        return language === 'ua' ? 'Закрите' : 'Closed';
      default:
        return status;
    }
  };



  const getCategoryName = (categoryId: string) => {
    const category = supportCategories.find(cat => cat.id === categoryId);
    return category ? category.title : categoryId;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ua' ? 'uk-UA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto', overflowX: 'auto' }}>
      {/* Page Header - Commented out as requested */}
      {/*
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {language === 'ua' ? 'Підтримка' : 'Support'}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {language === 'ua'
            ? 'Ми тут, щоб допомогти вам з будь-якими питаннями'
            : 'We\'re here to help you with any questions'
          }
        </Typography>
      </Box>
      */}

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="support tabs"
          variant="scrollable"
          scrollButtons={false}
          sx={{
            '& .MuiTabs-scroller': {
              overflowX: 'auto !important',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            },
            '& .MuiTab-root': {
              minWidth: { xs: 60, sm: 120 },
              '@media (max-width: 600px)': {
                '& .MuiTab-iconWrapper': {
                  marginBottom: 0,
                },
              },
            },
          }}
        >
          <Tab
            icon={<ContactMailIcon />}
            label={
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {language === 'ua' ? 'Зв\'язатися з нами' : 'Contact Us'}
              </Box>
            }
            value="contact"
          />
          <Tab
            icon={
              <Badge
                badgeContent={messages.length}
                color="primary"
                invisible={messages.length === 0}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    minWidth: '16px',
                    height: '16px',
                  },
                }}
              >
                <HistoryIcon />
              </Badge>
            }
            label={
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {language === 'ua' ? 'Історія повідомлень' : 'Message History'}
              </Box>
            }
            value="history"
          />
          <Tab
            icon={<HelpIcon />}
            label={
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {language === 'ua' ? 'Часті питання' : 'FAQ'}
              </Box>
            }
            value="faq"
          />
        </Tabs>
      </Box>

      <Box sx={{ mt: 3 }}>
        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <Box>
            {/* Success Message */}
            {showSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {language === 'ua' ? 'Повідомлення надіслано успішно!' : 'Message sent successfully!'}
              </Alert>
            )}

            {/* Contact Form */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {language === 'ua'
                    ? 'Не знайшли відповідь на своє питання? Напишіть нам, і ми допоможемо!'
                    : 'Didn\'t find the answer to your question? Write to us and we\'ll help!'
                  }
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={language === 'ua' ? 'Ваше повідомлення' : 'Your Message'}
                    multiline
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={language === 'ua' ? 'Опишіть вашу проблему детально...' : 'Describe your issue in detail...'}
                    required
                    fullWidth
                  />

                  <TextField
                    select
                    label={language === 'ua' ? 'Категорія питання' : 'Question Category'}
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    fullWidth
                  >
                    {supportCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.title}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SendIcon />}
                      disabled={!selectedCategory || !message.trim() || isSubmitting}
                      sx={{ minWidth: 150 }}
                    >
                      {isSubmitting
                        ? (language === 'ua' ? 'Надсилання...' : 'Sending...')
                        : (language === 'ua' ? 'Надіслати' : 'Send')
                      }
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {language === 'ua' ? 'Інші способи зв\'язку' : 'Other Contact Methods'}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <EmailIcon color="primary" />
                      <Box>
                        <Typography variant="h6">
                          {language === 'ua' ? 'Email' : 'Email'}
                        </Typography>
                        <Typography
                          component="a"
                          href={`mailto:${supportSettings.email}`}
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {supportSettings.email}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PhoneIcon color="primary" />
                      <Box>
                        <Typography variant="h6">
                          {language === 'ua' ? 'Телефон' : 'Phone'}
                        </Typography>
                        <Typography
                          component="a"
                          href={`tel:${supportSettings.phone}`}
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {supportSettings.phone}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <ClockIcon color="primary" />
                      <Box>
                        <Typography variant="h6">
                          {language === 'ua' ? 'Час роботи' : 'Working Hours'}
                        </Typography>
                        <Typography color="primary.main">
                          {supportSettings.working_hours}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {language === 'ua' ? 'Історія повідомлень' : 'Message History'}
              </Typography>

              {isLoadingMessages ? (
                <Typography>
                  {language === 'ua' ? 'Завантаження повідомлень...' : 'Loading messages...'}
                </Typography>
              ) : messages.length === 0 ? (
                <Typography color="text.secondary">
                  {language === 'ua' ? 'У вас поки що немає повідомлень підтримки' : 'You have no support messages yet'}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {messages.map((msg) => (
                    <Card
                      key={msg.id}
                      variant="outlined"
                      sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                      onClick={() => {
                        setSelectedMessage(msg);
                        setConversationOpen(true);
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon color="primary" />
                            <Typography variant="subtitle1">
                              {getCategoryName(msg.category)}
                            </Typography>
                          </Box>
                          <Chip
                            label={getStatusText(msg.status)}
                            color={
                              msg.status === 'resolved' ? 'success' :
                              msg.status === 'in_progress' ? 'warning' :
                              msg.status === 'new' ? 'info' : 'default'
                            }
                            size="small"
                          />
                        </Box>
                        <Typography sx={{ mb: 1 }}>
                          {msg.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(msg.created_at)}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {language === 'ua' ? 'Часті питання' : 'FAQ'}
              </Typography>

              {faqItems.map((faq, index) => (
                <Accordion key={index}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`faq${index}-content`}
                    id={`faq${index}-header`}
                  >
                    <Typography variant="h6">
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Support Chat Modal */}
      <SupportChatModal
        key={selectedMessage?.id || 'none'} // Force re-render when message changes
        open={conversationOpen}
        message={selectedMessage}
        onClose={() => {
          setConversationOpen(false);
          setSelectedMessage(null);
        }}
        onMessageSent={fetchMessages}
        isAdmin={false}
        onSendReply={undefined}
      />
    </Box>
  );
};

export default SupportPage;
